/**
 * Project picker — loads the list of projects on boot, populates the
 * `#project-picker` <select>, and persists the user's selection in
 * localStorage so it survives reloads.
 *
 * The server's `/api/v2/projects` endpoint is intentionally cross-tenant
 * and does NOT track "which project is current for this user" — that
 * notion is a UI preference and is owned here, not in the data model.
 */

import { apiFetchWithMeta, ApiClientError } from './api/client.js'
import { esc } from './components/shared.js'

/** Current project ID, used to scope fleet/work/kb API calls. */
let _currentProjectId: string | null = null

export function getCurrentProject(): string | null {
  return _currentProjectId
}

export function setCurrentProject(projectId: string | null): void {
  _currentProjectId = projectId
}

/**
 * localStorage key for the user's last-picked project. Restored on boot
 * by `initProjects`; written by `onProjectSwitch` whenever the picker
 * changes.
 */
const LAST_PROJECT_KEY = 'grz-project'

interface ProjectSummary {
  id: string
  name: string
}

/** Load the project picker dropdown and set the initial project. */
export async function initProjects(): Promise<void> {
  // The `<select id="project-picker">` may or may not be present depending
  // on which shell is rendered (the Galaxy sidebar doesn't include one
  // today). Setting `_currentProjectId` must happen either way, because
  // every /api/v2/{fleet,work,kb} call depends on it — if we short-circuit
  // on a missing element, the app boots with projectId=null and the server
  // rejects every data fetch with "X-Project-Id header is required".
  const sel = document.getElementById('project-picker') as HTMLSelectElement | null

  try {
    // Uses the shared API client so Authorization + envelope unwrap are
    // handled consistently. apiFetchWithMeta tolerates both the v2
    // envelope shape (`{data: Project[]}`) and the legacy bare-array
    // shape — enforced by the ST-no-apifetch-list structural test.
    // `/api/v2/projects` is cross-tenant and does not require
    // X-Project-Id, so it safely works before a project has been picked.
    // Canonical path is `/api/v2/projects` (NO trailing slash) —
    // Hono's `app.route('/api/v2/projects', sub)` + `sub.get('/')`
    // mounts the collection at the no-slash form. The trailing-slash
    // form 404s. The integration test helper in server-project.test.ts
    // strips trailing slashes before dispatching, so the tests appear
    // to pass either way — but real callers (SPA, CLI) must use the
    // no-slash form. A server-side 308 redirect from /projects/ →
    // /projects exists as belt-and-braces for direct callers.
    const { data: projects } = await apiFetchWithMeta<ProjectSummary[]>('/api/v2/projects')

    if (!projects || projects.length === 0) {
      if (sel) {
        sel.innerHTML = '<option value="">No projects</option>'
      }
      setCurrentProject(null)
      return
    }

    if (sel) {
      sel.innerHTML = projects
        .map((p) => `<option value="${esc(p.id)}">${esc(p.name)}</option>`)
        .join('')
    }

    // Restore the last-picked project from localStorage; fall back to
    // the first entry when no preference exists or the stored id is no
    // longer available (project deleted since last visit).
    const stored = readStoredProjectId()
    const first = projects[0]!
    const current: ProjectSummary = stored
      ? (projects.find((p) => p.id === stored) ?? first)
      : first
    setCurrentProject(current.id)
    if (sel) {
      sel.value = current.id
    }
    writeStoredProjectId(current.id)
  } catch (err) {
    const detail =
      err instanceof ApiClientError
        ? `${err.status} ${err.message}`
        : err instanceof Error
          ? err.message
          : String(err)
    console.error('[grizzly-web] Failed to load projects:', detail)
    if (sel) {
      sel.innerHTML = '<option value="">Load failed</option>'
    }
  }
}

/** Handle project picker change events from the HTML template. */
export async function onProjectSwitch(projectId: string): Promise<void> {
  const id = projectId || null
  setCurrentProject(id)
  writeStoredProjectId(id)
  const { dispatchRoute } = await import('./router.js')
  dispatchRoute()
}

function readStoredProjectId(): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT_KEY)
  } catch {
    // Access to localStorage can throw (private mode, storage disabled);
    // fall back to no-preference rather than crashing the picker.
    return null
  }
}

function writeStoredProjectId(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(LAST_PROJECT_KEY)
    } else {
      localStorage.setItem(LAST_PROJECT_KEY, id)
    }
  } catch {
    // Same rationale as readStoredProjectId — best-effort persistence.
  }
}
